// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { getClientProfile, updateClientProfile, uploadProfileImage, updateClientPassword } from '../services/api';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCamera, faUser, faLock, faUserCircle } from '@fortawesome/free-solid-svg-icons';
import Loader from '../components/common/Loader';

const Profile: React.FC = () => {
    const [profile, setProfile] = useState({
        name: '',
        prompt_display_name: '',
        email: '',
        phone: '',
        profile_image: null
    });

    const [passwordData, setPasswordData] = useState({
        current_password: '',
        new_password: '',
        confirm_password: ''
    });

    const [isLoading, setIsLoading] = useState(true);
    const [imagePreview, setImagePreview] = useState(null);

    useEffect(() => {
        fetchProfile();
    }, []);

    const fetchProfile = async () => {
        try {
            const clientId = localStorage.getItem('client_id');
            const data = await getClientProfile(clientId);
            setProfile(data);
            if (data.profile_image) {
                setImagePreview(data.profile_image);
            }
        } catch (error) {
            toast.error('Failed to load profile');
        } finally {
            setIsLoading(false);
        }
    };

    const handleGeneralSubmit = async (e) => {
        e.preventDefault();
        try {
            const clientId = localStorage.getItem('client_id');
            await updateClientProfile(clientId, profile);
            toast.success('Profile updated successfully');
        } catch (error) {
            toast.error('Failed to update profile');
        }
    };

    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        try {
            const clientId = localStorage.getItem('client_id');
            await updateClientPassword(clientId, passwordData);
            toast.success('Password updated successfully');
            setPasswordData({
                current_password: '',
                new_password: '',
                confirm_password: ''
            });
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Failed to update password');
        }
    };

    const handleImageUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        try {
            const clientId = localStorage.getItem('client_id');
            const formData = new FormData();
            formData.append('file', file);
            
            const response = await uploadProfileImage(clientId, formData);
            
            // Refresh the entire profile to get the latest data
            await fetchProfile();
            
            toast.success('Profile image updated');
        } catch (error) {
            toast.error('Failed to upload image');
        }
    };

    if (isLoading) {
        return (
            <div className="h-full min-h-[400px] relative">
                <Loader 
                    fullPage={false} 
                    text="Loading profile..." 
                    variant="pulse"
                    overlay={true} 
                />
            </div>
        );
    }

    return (
        <div className="">
            <h1 className="text-2xl font-bold mb-6">Profile Settings</h1>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {/* Profile Image Section */}
                <div className="bg-white rounded-lg shadow p-6">
                    <h2 className="text-xl font-semibold mb-4">
                        <FontAwesomeIcon icon={faCamera} className="mr-2" />
                        Profile Image
                    </h2>
                    <div className="flex flex-col items-center">
                        <div className="relative w-32 h-32 mb-4">
                            {imagePreview ? (
                                <img 
                                    src={imagePreview.startsWith('http') 
                                        ? imagePreview 
                                        : `${process.env.REACT_APP_API_URL}/${imagePreview}`}
                                    alt="Profile" 
                                    className="w-full h-full rounded-full object-cover"
                                    onError={(e) => {
                                        e.currentTarget.style.display = 'none';
                                        setImagePreview(null);
                                    }}
                                />
                            ) : (
                                <div className="w-full h-full rounded-full bg-gray-200 flex items-center justify-center">
                                    <FontAwesomeIcon icon={faUserCircle} className="text-gray-400 text-4xl" />
                                </div>
                            )}
                            <label className="absolute bottom-0 right-0 bg-blue-600 text-white p-2 rounded-full cursor-pointer">
                                <FontAwesomeIcon icon={faCamera} />
                                <input 
                                    type="file" 
                                    className="hidden" 
                                    onChange={handleImageUpload}
                                    accept="image/*"
                                />
                            </label>
                        </div>
                    </div>
                </div>

                {/* General Information Section */}
                <div className="bg-white rounded-lg shadow p-6">
                    <h2 className="text-xl font-semibold mb-4">
                        <FontAwesomeIcon icon={faUser} className="mr-2" />
                        General Information
                    </h2>
                    <form onSubmit={handleGeneralSubmit}>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Full Name</label>
                                <input
                                    type="text"
                                    value={profile.name}
                                    onChange={(e) => setProfile({...profile, name: e.target.value})}
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">AI Prompt Display Name</label>
                                <input
                                    type="text"
                                    value={profile.prompt_display_name || ''}
                                    onChange={(e) => setProfile({...profile, prompt_display_name: e.target.value})}
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                    placeholder="How your organization is referenced in AI responses"
                                />
                                <p className="mt-1 text-xs text-gray-500">
                                    Leave blank to use your full name in AI prompts.
                                </p>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Email</label>
                                <input
                                    type="email"
                                    value={profile.email}
                                    onChange={(e) => setProfile({...profile, email: e.target.value})}
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Phone</label>
                                <input
                                    type="tel"
                                    value={profile.phone}
                                    onChange={(e) => setProfile({...profile, phone: e.target.value})}
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                />
                            </div>
                            <button
                                type="submit"
                                className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                            >
                                Save Changes
                            </button>
                        </div>
                    </form>
                </div>

                {/* Security Section */}
                <div className="bg-white rounded-lg shadow p-6 md:col-span-2">
                    <h2 className="text-xl font-semibold mb-4">
                        <FontAwesomeIcon icon={faLock} className="mr-2" />
                        Security
                    </h2>
                    <form onSubmit={handlePasswordSubmit}>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Current Password</label>
                                <input
                                    type="password"
                                    value={passwordData.current_password}
                                    onChange={(e) => setPasswordData({...passwordData, current_password: e.target.value})}
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">New Password</label>
                                <input
                                    type="password"
                                    value={passwordData.new_password}
                                    onChange={(e) => setPasswordData({...passwordData, new_password: e.target.value})}
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700">Confirm New Password</label>
                                <input
                                    type="password"
                                    value={passwordData.confirm_password}
                                    onChange={(e) => setPasswordData({...passwordData, confirm_password: e.target.value})}
                                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                />
                            </div>
                        </div>
                        <button
                            type="submit"
                            className="mt-4 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                        >
                            Update Password
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default Profile; 